import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Save, Trash2, BellRing } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

export const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const EMPTY = { enabled: true, days: [0, 1, 2, 3, 4], start: "09:00", end: "17:00", reminders_enabled: true };

/** Admin — horaires du salarié + rappels de pointage (entrée/sortie oubliées, +15 min). */
export function WorkScheduleEditor({ userId }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [form, setForm] = useState(EMPTY);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [delay, setDelay] = useState(15);

  useEffect(() => {
    setLoading(true);
    api.get(`/tracking/employees/${userId}/schedule`).then((r) => {
      setDelay(r.data.reminder_delay_min || 15);
      if (r.data.schedule) { setForm({ ...EMPTY, ...r.data.schedule }); setExists(true); } else { setForm(EMPTY); setExists(false); }
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const toggleDay = (d) => setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d].sort() }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/tracking/employees/${userId}/schedule`, form);
      setForm({ ...EMPTY, ...r.data.schedule }); setExists(true);
      toast.success("Horaires enregistrés — rappels " + (form.reminders_enabled ? "activés" : "désactivés"));
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); } finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/tracking/employees/${userId}/schedule`); setForm(EMPTY); setExists(false); toast.success("Horaires supprimés"); } catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
  };

  if (loading) return <div className="bg-[#14101c] border border-white/10 rounded-xl p-4 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" /> Horaires…</div>;

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl p-4" data-testid="work-schedule-editor">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5 text-cyan-400" /> Horaires & rappels de pointage</h4>
        {exists && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-bold" data-testid="schedule-status">Définis</span>}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="schedule-days">
        {DAY_LABELS.map((l, i) => (
          <button key={l} type="button" onClick={() => toggleDay(i)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${form.days.includes(i) ? "bg-[#f7c935] text-[#0b0811] border-[#f7c935]" : "bg-[#0b0811] text-slate-400 border-white/10"}`} data-testid={`schedule-day-${i}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="text-[11px] text-slate-400">Début
          <input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1 w-full bg-[#0b0811] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" data-testid="schedule-start-input" />
        </label>
        <label className="text-[11px] text-slate-400">Fin
          <input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1 w-full bg-[#0b0811] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" data-testid="schedule-end-input" />
        </label>
      </div>
      <div className="flex items-center justify-between gap-2 mb-3 text-xs text-slate-300">
        <span className="flex items-center gap-1.5"><BellRing className="w-3.5 h-3.5 text-[#f7c935]" /> Rappels push si entrée/sortie non pointée ({delay} min après l'horaire)</span>
        <Switch checked={form.reminders_enabled} onCheckedChange={(v) => setForm({ ...form, reminders_enabled: v })} data-testid="schedule-reminders-switch" />
      </div>
      <div className="flex gap-2 justify-end">
        {exists && <Button size="sm" variant="ghost" onClick={remove} className="text-xs text-rose-300 h-8" data-testid="schedule-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer</Button>}
        <Button size="sm" onClick={save} disabled={saving || form.days.length === 0} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] text-xs font-bold h-8" data-testid="schedule-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</>}
        </Button>
      </div>
    </div>
  );
}
